import React, { useState, useEffect } from 'react';
import {
    X, User, Mail, Building, Briefcase, Calendar, Clock,
    CheckCircle, XCircle, AlertTriangle, AlertCircle, TrendingUp,
    TrendingDown, Pause, Award, Target, Activity, Download,
    ChevronRight, RefreshCw, BarChart3, FileText, Sparkles,
    BookOpen, ClipboardList, FolderOpen, Shield, Eye, MessageSquare
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import StatCard from './StatCard';
import ProgressRing from './ProgressRing';
import BarChart from './BarChart';
import LineChart from './LineChart';
import TaskDetailModal from './TaskDetailModal';

const EmployeeDetailModal = ({ employeeId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [trends, setTrends] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedTask, setSelectedTask] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [taskFilter, setTaskFilter] = useState('all');

    useEffect(() => {
        if (employeeId) {
            fetchEmployeeData();
        }
    }, [employeeId]);

    const fetchEmployeeData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [detailsRes, trendsRes] = await Promise.all([
                api.get(`/analytics/employees/${employeeId}/details`, { headers }),
                api.get(`/analytics/employees/${employeeId}/trends`, { headers })
            ]);

            setData(detailsRes.data);
            setTrends(trendsRes.data.trends);
        } catch (error) {
            console.error('Error fetching employee data:', error);
            toast.error('Failed to load employee details');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.post(
                `/analytics/employees/${employeeId}/export`,
                { format: 'csv' },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${data.employee.firstName}-${data.employee.lastName}-analytics.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Export downloaded');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    const getRiskBadge = (level) => {
        const badges = {
            high: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
            medium: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
            low: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }
        };
        const badge = badges[level] || badges.low;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}>
                <Icon className="w-4 h-4" />
                {level.charAt(0).toUpperCase() + level.slice(1)} Risk
            </span>
        );
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: { bg: 'bg-green-100', text: 'text-green-700' },
            in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' },
            pending: { bg: 'bg-gray-100', text: 'text-gray-700' },
            overdue: { bg: 'bg-red-100', text: 'text-red-700' },
            failed: { bg: 'bg-red-100', text: 'text-red-700' },
            pending_review: { bg: 'bg-amber-100', text: 'text-amber-700' }
        };
        const badge = badges[status] || badges.pending;
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                {status.replace(/_/g, ' ')}
            </span>
        );
    };

    const getComparisonBadge = (comparison) => {
        if (!comparison) return null;
        const badges = {
            fast: { bg: 'bg-green-100', text: 'text-green-700', label: 'Faster than avg' },
            slow: { bg: 'bg-red-100', text: 'text-red-700', label: 'Slower than avg' },
            average: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Average' },
            above_average: { bg: 'bg-green-100', text: 'text-green-700', label: 'Above avg' },
            below_average: { bg: 'bg-red-100', text: 'text-red-700', label: 'Below avg' }
        };
        const badge = badges[comparison] || badges.average;
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    const formatTime = (minutes) => {
        if (!minutes) return '-';
        if (minutes < 60) return `${Math.round(minutes)}m`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading employee details...</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const { employee, summary, tasks, scoreTrends, courseAnalytics, documentAnalytics, surveyAnalytics, complianceAnalytics, engagementMetrics, noticesViewed } = data;

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200 flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                                <User className="w-8 h-8 text-primary" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {employee.firstName} {employee.lastName}
                                </h2>
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                    <span className="flex items-center gap-1">
                                        <Mail className="w-4 h-4" />
                                        {employee.email}
                                    </span>
                                    {employee.department && (
                                        <span className="flex items-center gap-1">
                                            <Building className="w-4 h-4" />
                                            {employee.department}
                                        </span>
                                    )}
                                    {employee.position && (
                                        <span className="flex items-center gap-1">
                                            <Briefcase className="w-4 h-4" />
                                            {employee.position}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 mt-2">
                                    {getRiskBadge(summary.riskLevel)}
                                    <span className="text-sm text-gray-500">
                                        Engagement: <span className="font-semibold text-gray-700">{summary.engagementScore}%</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors disabled:opacity-50"
                            >
                                <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                                Export
                            </button>
                            <button
                                onClick={fetchEmployeeData}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <RefreshCw className="w-5 h-5 text-gray-500" />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="px-6 border-b border-gray-200">
                        <div className="flex gap-1">
                            {[
                                { id: 'overview', label: 'Overview', icon: BarChart3 },
                                { id: 'tasks', label: 'Tasks', icon: FileText },
                                { id: 'courses', label: 'Courses', icon: BookOpen },
                                { id: 'surveys', label: 'Surveys', icon: ClipboardList },
                                { id: 'documents', label: 'Documents', icon: FolderOpen },
                                { id: 'compliance', label: 'Compliance', icon: Shield },
                                { id: 'trends', label: 'Trends', icon: Activity },
                                { id: 'risk', label: 'Risk', icon: AlertTriangle }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 py-3 px-4 border-b-2 transition-colors font-medium ${activeTab === tab.id
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div className="space-y-6">
                                {/* Summary Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard
                                        title="Completion Rate"
                                        value={`${summary.completionRate}%`}
                                        subtitle={`${summary.completedTasks} of ${summary.totalTasks} tasks`}
                                        icon={Target}
                                        color={summary.completionRate >= 80 ? 'green' : summary.completionRate >= 50 ? 'amber' : 'red'}
                                    />
                                    <StatCard
                                        title="Avg Quiz Score"
                                        value={summary.avgQuizScore !== null ? `${summary.avgQuizScore}%` : '-'}
                                        subtitle="Across all quizzes"
                                        icon={Award}
                                        color={summary.avgQuizScore >= 80 ? 'green' : summary.avgQuizScore >= 60 ? 'amber' : 'red'}
                                    />
                                    <StatCard
                                        title="Total Time"
                                        value={formatTime(summary.totalTimeMinutes)}
                                        subtitle={`Avg ${formatTime(summary.avgTimePerTask)} per task`}
                                        icon={Clock}
                                        color="blue"
                                    />
                                    <StatCard
                                        title="Overdue Tasks"
                                        value={summary.overdueTasks}
                                        subtitle="Require attention"
                                        icon={AlertTriangle}
                                        color={summary.overdueTasks > 0 ? 'red' : 'green'}
                                    />
                                </div>

                                {/* Phase Progress */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Preboarding Progress */}
                                    <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-semibold text-amber-900">Preboarding</h3>
                                            <span className="text-xs font-medium bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                                                {summary.preboarding?.status || 'Not Started'}
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm text-amber-800">
                                                <span>Progress</span>
                                                <span className="font-semibold">{summary.preboarding?.progress || 0}%</span>
                                            </div>
                                            <div className="w-full h-2 bg-amber-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-amber-500 rounded-full"
                                                    style={{ width: `${summary.preboarding?.progress || 0}%` }}
                                                />
                                            </div>
                                            <p className="text-xs text-amber-700 mt-2">
                                                {summary.preboarding?.completed || 0} of {summary.preboarding?.total || 0} tasks completed
                                            </p>
                                        </div>
                                    </div>

                                    {/* Onboarding Progress */}
                                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-semibold text-blue-900">Onboarding</h3>
                                            <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                {summary.onboarding?.status || 'Not Started'}
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm text-blue-800">
                                                <span>Progress</span>
                                                <span className="font-semibold">{summary.onboarding?.progress || 0}%</span>
                                            </div>
                                            <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-blue-600 rounded-full"
                                                    style={{ width: `${summary.onboarding?.progress || 0}%` }}
                                                />
                                            </div>
                                            <p className="text-xs text-blue-700 mt-2">
                                                {summary.onboarding?.completed || 0} of {summary.onboarding?.total || 0} tasks completed
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Overview */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Task Status Distribution */}
                                    <div className="bg-gray-50 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Task Status</h3>
                                        <div className="flex items-center justify-around">
                                            <ProgressRing
                                                value={summary.completionRate}
                                                label="Completed"
                                                size={100}
                                            />
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-3 h-3 rounded-full bg-green-500" />
                                                    <span className="text-sm text-gray-600">Completed: {summary.completedTasks}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                                                    <span className="text-sm text-gray-600">In Progress: {summary.inProgressTasks}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-3 h-3 rounded-full bg-gray-400" />
                                                    <span className="text-sm text-gray-600">Pending: {summary.pendingTasks}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-3 h-3 rounded-full bg-red-500" />
                                                    <span className="text-sm text-gray-600">Overdue: {summary.overdueTasks}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Engagement Metrics */}
                                    <div className="bg-gray-50 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Engagement Metrics</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-600">Engagement Score</span>
                                                    <span className="font-semibold">{summary.engagementScore}%</span>
                                                </div>
                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${summary.engagementScore >= 70 ? 'bg-green-500' :
                                                            summary.engagementScore >= 40 ? 'bg-amber-500' : 'bg-red-500'
                                                            }`}
                                                        style={{ width: `${summary.engagementScore}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 pt-2">
                                                <div className="text-center p-3 bg-white rounded-lg">
                                                    <p className="text-2xl font-bold text-gray-900">{summary.totalPauses}</p>
                                                    <p className="text-xs text-gray-500">Total Pauses</p>
                                                </div>
                                                <div className="text-center p-3 bg-white rounded-lg">
                                                    <p className="text-2xl font-bold text-gray-900">{summary.autoPauseRatio}%</p>
                                                    <p className="text-xs text-gray-500">Auto-Pause Rate</p>
                                                </div>
                                                <div className="text-center p-3 bg-white rounded-lg">
                                                    <p className="text-2xl font-bold text-gray-900">{summary.tasksWithRetries}</p>
                                                    <p className="text-xs text-gray-500">Tasks w/ Retries</p>
                                                </div>
                                                <div className="text-center p-3 bg-white rounded-lg">
                                                    <p className="text-2xl font-bold text-gray-900">{summary.riskScore}</p>
                                                    <p className="text-xs text-gray-500">Risk Score</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Recent Tasks */}
                                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                    <div className="p-4 border-b border-gray-200">
                                        <h3 className="font-semibold text-gray-800">Recent Tasks</h3>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {tasks.slice(0, 5).map(task => (
                                            <div
                                                key={task.assignmentId}
                                                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                                onClick={() => setSelectedTask(task)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-medium text-gray-900">{task.title}</p>
                                                            {getStatusBadge(task.status)}
                                                        </div>
                                                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                                            <span className="capitalize">{task.taskType}</span>
                                                            {task.performance.score !== null && (
                                                                <span>Score: {task.performance.score}%</span>
                                                            )}
                                                            <span>Time: {formatTime(task.performance.timeMinutes)}</span>
                                                            {getComparisonBadge(task.comparison.timeComparison)}
                                                        </div>
                                                    </div>
                                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tasks Tab */}
                        {activeTab === 'tasks' && (
                            <div className="space-y-4">
                                {/* Task Filters */}
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm text-gray-500">{tasks.length} total tasks</span>

                                    <div className="flex bg-gray-100 rounded-lg p-1">
                                        <button
                                            onClick={() => setTaskFilter('all')}
                                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${taskFilter === 'all'
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            All
                                        </button>
                                        <button
                                            onClick={() => setTaskFilter('preboarding')}
                                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${taskFilter === 'preboarding'
                                                ? 'bg-white text-amber-700 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            Preboarding
                                        </button>
                                        <button
                                            onClick={() => setTaskFilter('onboarding')}
                                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${taskFilter === 'onboarding'
                                                ? 'bg-white text-blue-700 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            Onboarding
                                        </button>
                                    </div>
                                </div>

                                {/* Task List */}
                                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Phase</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Score</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Time</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">vs Avg</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pauses</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attempts</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {tasks
                                                .filter(t => taskFilter === 'all' || t.phase === taskFilter)
                                                .map(task => (
                                                    <tr
                                                        key={task.assignmentId}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                        onClick={() => setSelectedTask(task)}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium text-gray-900 truncate max-w-xs">{task.title}</p>
                                                            <p className="text-xs text-gray-500">{formatDate(task.dates.assigned)}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${task.phase === 'preboarding'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                                }`}>
                                                                {task.phase === 'preboarding' ? 'Preboarding' : 'Onboarding'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="capitalize text-sm text-gray-600">{task.taskType}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {getStatusBadge(task.status)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {task.performance.score !== null ? (
                                                                <span className={`font-medium ${task.performance.score >= 80 ? 'text-green-600' :
                                                                    task.performance.score >= 60 ? 'text-amber-600' : 'text-red-600'
                                                                    }`}>
                                                                    {task.performance.score}%
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                                                            {formatTime(task.performance.timeMinutes)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {getComparisonBadge(task.comparison.timeComparison)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={task.performance.pauseCount > 5 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                                                                {task.performance.pauseCount}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={task.performance.attempts > 1 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                                                                {task.performance.attempts}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Courses Tab */}
                        {activeTab === 'courses' && courseAnalytics && (
                            <div className="space-y-6">
                                {/* Course Summary */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard title="Enrolled" value={courseAnalytics.summary?.enrolled ?? 0} icon={BookOpen} color="blue" />
                                    <StatCard title="Completed" value={courseAnalytics.summary?.completed ?? 0} icon={CheckCircle} color="green" />
                                    <StatCard title="In Progress" value={courseAnalytics.summary?.inProgress ?? 0} icon={Clock} color="amber" />
                                    <StatCard title="Completion Rate" value={`${courseAnalytics.summary?.completionRate ?? 0}%`} icon={Target} color={courseAnalytics.summary?.completionRate >= 80 ? 'green' : 'amber'} />
                                </div>

                                {/* Course List */}
                                {courseAnalytics.courses?.length > 0 ? (
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="p-4 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-800">Course Enrollments</h3>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {courseAnalytics.courses.map(course => (
                                                <div key={course.enrollmentId} className="p-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div>
                                                            <p className="font-medium text-gray-900">{course.courseName}</p>
                                                            <p className="text-xs text-gray-500 capitalize">{course.courseType} • Enrolled {formatDate(course.enrollmentDate)}</p>
                                                        </div>
                                                        {getStatusBadge(course.status)}
                                                    </div>
                                                    <div className="flex items-center gap-3 mt-2">
                                                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                            <div className={`h-full rounded-full ${course.progressPercentage >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                                                style={{ width: `${course.progressPercentage}%` }} />
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-700 min-w-[40px] text-right">{course.progressPercentage}%</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">{course.completedItems} of {course.totalItems} items completed</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-500">No course enrollments found.</div>
                                )}
                            </div>
                        )}

                        {/* Surveys Tab */}
                        {activeTab === 'surveys' && surveyAnalytics && (
                            <div className="space-y-6">
                                {/* Survey Summary */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard title="Assigned" value={surveyAnalytics.summary?.totalAssigned ?? 0} icon={ClipboardList} color="blue" />
                                    <StatCard title="Completed" value={surveyAnalytics.summary?.completed ?? 0} icon={CheckCircle} color="green" />
                                    <StatCard title="Response Rate" value={`${surveyAnalytics.summary?.responseRate ?? 0}%`} icon={Target} color={surveyAnalytics.summary?.responseRate >= 80 ? 'green' : 'amber'} />
                                    <StatCard title="Avg Sentiment" value={surveyAnalytics.summary?.avgSentiment !== null ? surveyAnalytics.summary.avgSentiment.toFixed(1) : '-'} icon={Activity} color={surveyAnalytics.summary?.avgSentiment >= 0.6 ? 'green' : surveyAnalytics.summary?.avgSentiment >= 0.3 ? 'amber' : 'red'} />
                                </div>

                                {/* Sentiment Trend */}
                                {surveyAnalytics.summary?.sentimentTrend?.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Sentiment Over Time</h3>
                                        <LineChart
                                            data={surveyAnalytics.summary.sentimentTrend.map(t => ({
                                                date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: Math.round(t.sentiment * 100)
                                            }))}
                                            height={180}
                                            lineColor="#8b5cf6"
                                        />
                                    </div>
                                )}

                                {/* Survey List */}
                                {surveyAnalytics.surveys?.length > 0 ? (
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="p-4 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-800">Survey Responses</h3>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {surveyAnalytics.surveys.map(survey => (
                                                <div key={survey.instanceId} className="p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-medium text-gray-900">{survey.surveyTitle}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {survey.answeredQuestions}/{survey.totalQuestions} questions answered
                                                                {survey.avgSentiment !== null && ` • Sentiment: ${(survey.avgSentiment * 100).toFixed(0)}%`}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {survey.isAnonymous && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Anonymous</span>}
                                                            {getStatusBadge(survey.status)}
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-1">Deployed {formatDate(survey.deployedAt)}{survey.completedAt && ` • Completed ${formatDate(survey.completedAt)}`}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-500">No surveys assigned.</div>
                                )}
                            </div>
                        )}

                        {/* Documents Tab */}
                        {activeTab === 'documents' && documentAnalytics && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <StatCard title="Files Accessed" value={documentAnalytics.totalAccessed ?? 0} icon={FolderOpen} color="blue" />
                                    <StatCard title="Total Views" value={documentAnalytics.files?.reduce((s, f) => s + f.viewCount, 0) ?? 0} icon={Eye} color="green" />
                                    <StatCard title="Acknowledged" value={documentAnalytics.files?.filter(f => f.acknowledgedAt).length ?? 0} icon={CheckCircle} color="green" />
                                </div>

                                {documentAnalytics.files?.length > 0 ? (
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="p-4 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-800">File Activity</h3>
                                        </div>
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Views</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acknowledged</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Last Access</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {documentAnalytics.files.map(file => (
                                                    <tr key={file.fileId} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium text-gray-900 truncate max-w-xs">{file.fileName}</p>
                                                            <p className="text-xs text-gray-500 capitalize">{file.fileCategory || '-'}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm text-gray-600">{file.fileType || '-'}</td>
                                                        <td className="px-4 py-3 text-center font-medium text-gray-700">{file.viewCount}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            {file.acknowledgedAt ? (
                                                                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                                                    <CheckCircle className="w-3 h-3" /> Yes
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-sm text-gray-500">{formatDate(file.lastAccessed)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-500">No document activity found.</div>
                                )}
                            </div>
                        )}

                        {/* Compliance Tab */}
                        {activeTab === 'compliance' && complianceAnalytics && (
                            <div className="space-y-6">
                                {/* Compliance Summary */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <StatCard title="Status" value={complianceAnalytics.isCompliant ? 'Compliant' : 'Non-Compliant'} icon={Shield} color={complianceAnalytics.isCompliant ? 'green' : 'red'} />
                                    <StatCard title="Active Violations" value={complianceAnalytics.activeViolations ?? 0} icon={AlertTriangle} color={complianceAnalytics.activeViolations > 0 ? 'red' : 'green'} />
                                    <StatCard title="Resolved" value={complianceAnalytics.resolvedViolations ?? 0} icon={CheckCircle} color="green" />
                                    <StatCard title="Total" value={complianceAnalytics.totalViolations ?? 0} icon={BarChart3} color="blue" />
                                </div>

                                {/* Compliance Timeline */}
                                {complianceAnalytics.history?.length > 0 ? (
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="p-4 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-800">Violation History</h3>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {complianceAnalytics.history.map(v => (
                                                <div key={v.violationId} className="p-4 flex items-start gap-4">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${v.status === 'active' ? 'bg-red-100' : 'bg-green-100'}`}>
                                                        {v.status === 'active' ? <AlertTriangle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <p className="font-medium text-gray-900">{v.taskTitle}</p>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{v.status}</span>
                                                        </div>
                                                        <p className="text-sm text-gray-600 mt-0.5">{v.violationType} • {v.daysOverdue} day{v.daysOverdue !== 1 ? 's' : ''} overdue</p>
                                                        <p className="text-xs text-gray-400 mt-1">Created {formatDate(v.violationDate)}{v.resolvedAt && ` • Resolved ${formatDate(v.resolvedAt)}`}</p>
                                                        {v.notes && <p className="text-xs text-gray-500 mt-1 italic">{v.notes}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <CheckCircle className="w-10 h-10 text-green-400 mb-3" />
                                        <p className="text-gray-600 font-medium">No compliance violations</p>
                                        <p className="text-sm text-gray-400">This employee has a clean compliance record.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Trends Tab */}
                        {activeTab === 'trends' && trends && (
                            <div className="space-y-6">
                                {/* Score Trends */}
                                {scoreTrends && scoreTrends.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Quiz Score Trends</h3>
                                        <LineChart
                                            data={scoreTrends.map(t => ({
                                                date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: t.score
                                            }))}
                                            height={200}
                                            lineColor="#6366f1"
                                        />
                                    </div>
                                )}

                                {/* Preboarding Trends */}
                                {trends.preboardingCompletions && trends.preboardingCompletions.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Preboarding Task Completions</h3>
                                        <LineChart
                                            data={trends.preboardingCompletions.map(t => ({
                                                date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: t.completions
                                            }))}
                                            height={200}
                                            lineColor="#f59e0b" // Amber
                                        />
                                    </div>
                                )}

                                {/* Onboarding Trends */}
                                {trends.onboardingCompletions && trends.onboardingCompletions.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Onboarding Task Completions</h3>
                                        <LineChart
                                            data={trends.onboardingCompletions.map(t => ({
                                                date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: t.completions
                                            }))}
                                            height={200}
                                            lineColor="#2563eb" // Blue
                                        />
                                    </div>
                                )}

                                {/* Pause Patterns */}
                                {trends.pausePatterns && trends.pausePatterns.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Pause Patterns</h3>
                                        <BarChart
                                            data={trends.pausePatterns.slice(-14).map(t => ({
                                                label: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: t.totalPauses,
                                                color: t.autoPauses > t.totalPauses / 2 ? '#f59e0b' : '#6366f1'
                                            }))}
                                            height={180}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Risk Analysis Tab */}
                        {activeTab === 'risk' && (
                            <div className="space-y-6">
                                {/* Risk Score Overview */}
                                <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="font-semibold text-gray-800">Risk Assessment</h3>
                                            <p className="text-sm text-gray-500">Based on task performance and engagement patterns</p>
                                        </div>
                                        {getRiskBadge(summary.riskLevel)}
                                    </div>

                                    <div className="flex items-center gap-8">
                                        <ProgressRing
                                            value={summary.riskScore}
                                            max={100}
                                            size={140}
                                            label="Risk Score"
                                            color={summary.riskLevel === 'high' ? '#ef4444' : summary.riskLevel === 'medium' ? '#f59e0b' : '#22c55e'}
                                        />

                                        <div className="flex-1">
                                            <h4 className="font-medium text-gray-800 mb-3">Risk Factors</h4>
                                            {summary.riskFactors.length > 0 ? (
                                                <div className="space-y-2">
                                                    {summary.riskFactors.map((factor, index) => (
                                                        <div key={index} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                                                            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                                            <div>
                                                                <p className="font-medium text-red-800">{factor.factor}</p>
                                                                <p className="text-sm text-red-600">{factor.detail}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                                    <p className="text-green-800">No significant risk factors identified</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Detailed Metrics */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Completion Analysis */}
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Completion Analysis</h3>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Overall Rate</span>
                                                <span className={`font-semibold ${summary.completionRate >= 80 ? 'text-green-600' :
                                                    summary.completionRate >= 50 ? 'text-amber-600' : 'text-red-600'
                                                    }`}>{summary.completionRate}%</span>
                                            </div>

                                            {/* Phase Breakdown */}
                                            {summary.preboarding && (
                                                <div className="flex items-center justify-between pl-4 border-l-2 border-amber-200">
                                                    <span className="text-gray-500 text-sm">Preboarding</span>
                                                    <span className="font-medium text-amber-700">{summary.preboarding.progress}%</span>
                                                </div>
                                            )}
                                            {summary.onboarding && (
                                                <div className="flex items-center justify-between pl-4 border-l-2 border-blue-200">
                                                    <span className="text-gray-500 text-sm">Onboarding</span>
                                                    <span className="font-medium text-blue-700">{summary.onboarding.progress}%</span>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                                <span className="text-gray-600">Overdue Tasks</span>
                                                <span className={`font-semibold ${summary.overdueTasks > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {summary.overdueTasks}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Tasks with Retries</span>
                                                <span className={`font-semibold ${summary.tasksWithRetries > 2 ? 'text-amber-600' : 'text-gray-700'}`}>
                                                    {summary.tasksWithRetries}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Engagement Analysis */}
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Engagement Analysis</h3>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Engagement Score</span>
                                                <span className={`font-semibold ${summary.engagementScore >= 70 ? 'text-green-600' :
                                                    summary.engagementScore >= 40 ? 'text-amber-600' : 'text-red-600'
                                                    }`}>{summary.engagementScore}%</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Auto-Pause Ratio</span>
                                                <span className={`font-semibold ${summary.autoPauseRatio > 50 ? 'text-amber-600' : 'text-gray-700'}`}>
                                                    {summary.autoPauseRatio}%
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Avg Quiz Score</span>
                                                <span className={`font-semibold ${summary.avgQuizScore >= 80 ? 'text-green-600' :
                                                    summary.avgQuizScore >= 60 ? 'text-amber-600' : 'text-red-600'
                                                    }`}>{summary.avgQuizScore !== null ? `${summary.avgQuizScore}%` : '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* AI Recommendations */}
                                <div className="bg-gradient-to-br from-primary/5 to-purple-50 rounded-xl border border-primary/20 p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Sparkles className="w-5 h-5 text-primary" />
                                        <h3 className="font-semibold text-gray-800">AI Recommendations</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {summary.riskLevel === 'high' && (
                                            <p className="text-sm text-gray-700">
                                                This employee shows signs of struggling. Consider scheduling a check-in to understand any blockers
                                                and provide additional support for overdue tasks.
                                            </p>
                                        )}
                                        {summary.autoPauseRatio > 50 && (
                                            <p className="text-sm text-gray-700">
                                                High auto-pause rate suggests possible distractions or task complexity issues.
                                                Consider breaking down longer tasks or providing a quieter work environment.
                                            </p>
                                        )}
                                        {summary.avgQuizScore !== null && summary.avgQuizScore < 70 && (
                                            <p className="text-sm text-gray-700">
                                                Quiz scores are below average. Review which topics need reinforcement
                                                and consider additional training materials.
                                            </p>
                                        )}
                                        {summary.riskLevel === 'low' && (
                                            <p className="text-sm text-gray-700">
                                                This employee is performing well. Consider recognizing their progress
                                                and potentially assigning more challenging tasks.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Task Detail Modal */}
            {selectedTask && (
                <TaskDetailModal
                    employeeId={employeeId}
                    assignmentId={selectedTask.assignmentId}
                    onClose={() => setSelectedTask(null)}
                />
            )}
        </>
    );
};

export default EmployeeDetailModal;
